/**
 * WEBSOCKET / SIGNALR FAN SERVICE: QUICK REACTION (REACCIÓN RÁPIDA 1 VS 1)
 * Wrapper de comunicación en tiempo real para el Aficionado Móvil (Jugador 1 o Jugador 2).
 * Gestiona la conexión persistente con SignalR Hub para recibir cuenta regresiva sincronizada,
 * ventana de espera aleatoria, señal de disparo ('quick_reaction_trigger_active'), faltas tempranas,
 * pantalla de fin de partida y el evento de desalojo forzoso 'kicked_out' (Patrón Tacos).
 * 
 * @module utilities/WS/QuickReactionFanWS
 */

import SignalRService from './SignalRService';
import { ClearPlayerSessionData } from '../BL/QuickReaction';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes generales.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión exitosa registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de reconexión en progreso registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Normaliza la clave del rol de conexión para el Hub de SignalR.
 * 
 * @param {string} [playerSide=''] - Lado o bando del jugador ('player1' | 'player2' | 'J1' | 'J2' | 'fan').
 * @returns {string} Rol de conexión formal ('quickreaction:player1' | 'quickreaction:player2' | 'quickreaction:fan').
 */
const resolvePlayerRole = (playerSide = '') => {
    const normalized = String(playerSide || '').toLowerCase().trim();
    if (normalized === 'player1' || normalized === 'j1' || normalized === 'p1' || normalized === '1' || normalized === 'left') {
        return 'quickreaction:player1';
    }
    if (normalized === 'player2' || normalized === 'j2' || normalized === 'p2' || normalized === '2' || normalized === 'right') {
        return 'quickreaction:player2';
    }
    return 'quickreaction:fan';
};

/**
 * Conecta el dispositivo móvil del aficionado al Hub de SignalR con el rol 'quickreaction:player1', 'quickreaction:player2' o 'quickreaction:fan'.
 * 
 * Gestiona de forma prioritaria el evento 'kicked_out' ({ reason: "CHARACTER_REASSIGNED" }):
 * Cuando otro aficionado toma el mismo personaje, se ejecuta el callback de desalojo,
 * se limpia el sessionStorage de la sala y se notifica al componente visual.
 * 
 * @param {Function} [onIncomingMessage=() => {}] - Callback para procesar mensajes y eventos emitidos por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string} [roomId=''] - Identificador GUID de la sala para filtrar el tráfico de eventos.
 * @param {string} [playerSide=''] - Lado asignado al participante ('player1' | 'player2' | 'J1' | 'J2').
 * @param {Function} [onKickedOut=() => {}] - Callback específico ejecutado al recibir el evento 'kicked_out'.
 */
export const ConnectWebSocketForQuickReactionFan = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = '',
    playerSide = '',
    onKickedOut = () => {}
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala y detección de kicked_out
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        // 1. FILTRADO ESTRICTO POR ROOM ID (AISLAMIENTO MULTI-TENANT)
        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        const eventType = String(messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();

        // 2. MANEJO PRIORITARIO DEL EVENTO KICKED_OUT (PATRÓN TACOS)
        if (eventType === 'kicked_out') {
            console.warn('[QuickReactionFanWS] Evento kicked_out recibido para esta sala:', messageEnvelope);

            // Limpieza inmediata y obligatoria del sessionStorage para esta sala
            if (roomId) {
                ClearPlayerSessionData(roomId);
            }

            // Invocar el callback específico de desalojo
            if (typeof onKickedOut === 'function') {
                try {
                    onKickedOut(messageEnvelope);
                } catch (kickCbErr) {
                    console.error('[QuickReactionFanWS] Error en onKickedOut callback:', kickCbErr);
                }
            }

            // También despachar al handler general
            if (typeof onIncomingMessage === 'function') {
                onIncomingMessage(messageEnvelope);
            }
            return;
        }

        // 3. DESPACHAR AL HANDLER GENERAL
        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(messageEnvelope);
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    const connectionRole = resolvePlayerRole(playerSide);

    SignalRService.connect({
        tipo: connectionRole,
        roomId: roomId || '',
        playerSide: playerSide || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[QuickReactionFanWS] Error al conectar el aficionado con SignalR Hub:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del aficionado móvil y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForQuickReactionFan = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[QuickReactionFanWS] Error al desconectar la sesión de SignalR del aficionado:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForQuickReactionFan,
    DisconnectWebSocketForQuickReactionFan
};
